import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import type { AdminAccountOverviewRow, AdminRouteMap } from "../types";

type AdminAccountsPageProps = {
  accounts: AdminAccountOverviewRow[];
  dateOptions: AdminTableDateOptions;
  adminRoutes: AdminRouteMap;
};

export const AdminAccountsPage: React.FC<AdminAccountsPageProps> = ({
  accounts,
  dateOptions,
  adminRoutes,
}) => (
  <DashboardPageLayout
    title="Accounts"
    subtitle="Overview of users and businesses (last 200). Click a row for billing, quotes, and chats."
  >
    <Card>
      <CardHeader>
        <CardTitle>Users and businesses</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium">Name</th>
                <th className="text-left py-3 px-4 font-medium">Email</th>
                <th className="text-left py-3 px-4 font-medium">
                  User created
                </th>
                <th className="text-left py-3 px-4 font-medium">Business</th>
                <th className="text-left py-3 px-4 font-medium">Billing</th>
                <th className="text-right py-3 px-4 font-medium">Quotes</th>
                <th className="text-right py-3 px-4 font-medium">Chats</th>
                <th className="text-left py-3 px-4 font-medium">Admin</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((row) => {
                const href = adminRoutes.adminUser.replace(
                  ":id",
                  String(row.id),
                );
                const go = () => {
                  window.location.assign(href);
                };
                return (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    tabIndex={0}
                    role="link"
                    aria-label={`View account for ${row.email}`}
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        go();
                      }
                    }}
                  >
                    <td className="py-3 px-4">
                      {row.ownerName ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">{row.email}</td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {formatAdminTableDate(row.createdAt, dateOptions)}
                    </td>
                    <td className="py-3 px-4 max-w-56">
                      {row.business ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium truncate block">
                            {row.business.businessName ??
                              row.business.subdomain}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {row.business.subdomain}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 capitalize">
                      {row.business?.billingStatus ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.quoteCount}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.chatSessionCount}
                    </td>
                    <td className="py-3 px-4">{row.admin ? "Yes" : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  </DashboardPageLayout>
);
